import { Sidepanel, Spinner2 } from 'components';
import { Api } from 'lib';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { ContentHeader } from 'screens/Admin/components';

const InfoRow = ({ label, children, muted }) => (
  <div className="flex items-baseline justify-between py-[5px]">
    <span className="text-[12px] text-[#8993a4] select-none">{label}</span>
    <span className={`text-[13px] font-medium ${muted ? 'text-[#8993a4]' : 'text-[#2d3643]'}`}>
      {children}
    </span>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold text-[#8993a4] uppercase tracking-[0.06em] mb-2">
    {children}
  </p>
);

const statusColors = {
  pending: 'bg-[#FEF9C3] text-[#854D0E]',
  approved: 'bg-[#DBEAFE] text-[#1E40AF]',
  rejected: 'bg-[#FEE2E2] text-[#991B1B]',
  completed: 'bg-[#D1FAE5] text-[#065F46]'
};

const migrationStatusColors = {
  queued: 'bg-[#FEF9C3] text-[#854D0E]',
  processing: 'bg-[#DBEAFE] text-[#1E40AF]',
  completed: 'bg-[#D1FAE5] text-[#065F46]',
  failed: 'bg-[#FEE2E2] text-[#991B1B]'
};

const StatusBadge = ({ status }) => (
  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${statusColors[status] || 'bg-[#F3F4F6] text-[#374151]'}`}>
    {status ? status.charAt(0).toUpperCase() + status.slice(1) : '-'}
  </span>
);

const MigrationBadge = ({ status }) => (
  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${migrationStatusColors[status] || 'bg-[#F3F4F6] text-[#374151]'}`}>
    {status ? status.charAt(0).toUpperCase() + status.slice(1) : '-'}
  </span>
);

const getMigrationStatus = (request) => {
  if (!request || request.type !== 'replacement' || request.status !== 'completed') {
    return null;
  }

  if (request.migrationStatus) {
    return request.migrationStatus;
  }

  return request.subscriptionsMigrated ? 'completed' : 'queued';
};

const StripeRequestPanel = ({ request, visible, closePanel, onActionDone }) => {
  const [loading, setLoading] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [linkExpiry, setLinkExpiry] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const dispatch = useDispatch();

  if (!request) return null;

  const migrationView = migrateResult || request;
  const isPending = request.status === 'pending';
  const isReplacement = request.type === 'replacement';
  const isDeletion = request.type === 'deletion';
  const migrationStatus = getMigrationStatus(migrationView);
  const migrationCompleted = migrationStatus === 'completed';
  const migrationAttempts = migrationView.migrationAttemptCount || 0;
  const migrationMaxAttempts = migrationView.migrationMaxAttempts || 0;

  async function handleGenerateLink() {
    setLoading(true);
    try {
      const res = await Api.post('/admin/teams/stripe/generateReplacementLink', {
        requestId: request._id
      });
      if (res?.data) {
        setGeneratedLink(res.data.oauthUrl);
        setLinkExpiry(res.data.expiresAt);
        dispatch({ type: 'HANDLE_RES', payload: { res: 'Replacement link generated successfully' } });
        onActionDone();
      }
    } catch {
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Failed to generate replacement link' } });
    }
    setLoading(false);
  }

  async function handleApproveDeletion() {
    setLoading(true);
    try {
      const res = await Api.post('/admin/teams/stripe/approveDeleteRequest', {
        requestId: request._id
      });
      if (res) {
        dispatch({ type: 'HANDLE_RES', payload: { res: 'Stripe account disconnected successfully' } });
        onActionDone();
      }
    } catch {
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Failed to approve deletion request' } });
    }
    setLoading(false);
  }

  async function handleReject() {
    setLoading(true);
    try {
      const res = await Api.post('/admin/teams/stripe/rejectStripeRequest', {
        requestId: request._id,
        reason: rejectReason.trim() || undefined
      });
      if (res) {
        dispatch({ type: 'HANDLE_RES', payload: { res: 'Request rejected' } });
        setShowRejectForm(false);
        setRejectReason('');
        onActionDone();
      }
    } catch {
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Failed to reject request' } });
    }
    setLoading(false);
  }

  async function handleMigrate() {
    setMigrating(true);
    setMigrateResult(null);
    try {
      const res = await Api.post('/admin/teams/stripe/migrateSubscriptions', {
        requestId: request._id
      });
      const migration = res?.data?.migration || res?.data;
      if (migration) {
        const mergedMigration = { ...request, ...migration };
        setMigrateResult(mergedMigration);

        if (migration.migrationStatus === 'completed') {
          dispatch({
            type: 'HANDLE_RES',
            payload: { res: 'Subscriptions migrated successfully' }
          });
        } else if (migration.migrationStatus === 'failed') {
          dispatch({
            type: 'HANDLE_RES',
            payload: { res: 'Migration failed after retries. Please retry manually.' }
          });
        } else {
          dispatch({
            type: 'HANDLE_RES',
            payload: { res: 'Migration attempt started. It will continue automatically.' }
          });
        }

        if (onActionDone) {
          onActionDone({ closePanel: false });
        }
      } else {
        dispatch({ type: 'HANDLE_RES', payload: { res: 'Migration response missing details' } });
      }
    } catch {
      dispatch({ type: 'HANDLE_RES', payload: { res: 'Failed to run subscription migration' } });
    }
    setMigrating(false);
  }

  function formatDateTime(value) {
    if (!value) return '-';
    return new Date(value).toLocaleString();
  }

  function renderMigrationSummary() {
    if (migrationCompleted) {
      return (
        <div className="flex items-center gap-2 px-4 py-3 bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl">
          <span className="w-2 h-2 rounded-full bg-[#16A34A]" />
          <span className="text-[13px] font-medium text-[#15803D]">
            Migration completed successfully
          </span>
        </div>
      );
    }

    if (migrationStatus === 'failed') {
      return (
        <div className="flex items-center gap-2 px-4 py-3 bg-[#FEF2F2] border border-[#FCA5A5] rounded-xl">
          <span className="w-2 h-2 rounded-full bg-[#DC2626]" />
          <span className="text-[13px] font-medium text-[#991B1B]">
            Migration failed and is blocked until resolved
          </span>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-[#FEF9C3] border border-[#FDE047] rounded-xl">
        <span className={`w-2 h-2 rounded-full ${migrationStatus === 'processing' ? 'bg-[#2563EB] animate-pulse' : 'bg-[#FBBF24]'}`} />
        <span className="text-[13px] font-medium text-[#854D0E]">
          {migrationStatus === 'processing'
            ? 'Migration in progress'
            : 'Migration queued for automatic retry'}
        </span>
      </div>
    );
  }

  function renderMigrationDetailRows() {
    return (
      <div className="bg-white border border-[#e5e7eb] rounded-xl px-5 py-3 space-y-0.5">
        <InfoRow label="Migration Status">
          <MigrationBadge status={migrationStatus || 'queued'} />
        </InfoRow>
        <InfoRow label="Attempts" muted>
          {migrationAttempts}{migrationMaxAttempts ? ` / ${migrationMaxAttempts}` : ''}
        </InfoRow>
        <InfoRow label="Total Subs" muted>{migrationView.migrationTotal || 0}</InfoRow>
        <InfoRow label="Migrated" muted>{migrationView.migrationMigrated || 0}</InfoRow>
        <InfoRow label="Skipped" muted>{migrationView.migrationSkipped || 0}</InfoRow>
        <InfoRow label="Failed" muted>{migrationView.migrationFailed || 0}</InfoRow>
        <InfoRow label="Started At" muted>{formatDateTime(migrationView.migrationStartedAt)}</InfoRow>
        <InfoRow label="Last Attempt" muted>
          {formatDateTime(migrationView.migrationLastAttemptAt)}
        </InfoRow>
        <InfoRow label="Next Retry" muted>{formatDateTime(migrationView.migrationNextRetryAt)}</InfoRow>
        {migrationView.migrationCompletedAt && (
          <InfoRow label="Completed At" muted>
            {formatDateTime(migrationView.migrationCompletedAt)}
          </InfoRow>
        )}
        {migrationView.migrationLastError && (
          <div className="mt-2 p-2 rounded bg-[#FEF2F2] border border-[#FCA5A5]">
            <p className="text-[11px] text-[#991B1B] font-mono break-all">
              {migrationView.migrationLastError}
            </p>
          </div>
        )}
      </div>
    );
  }

  function renderMigrationResultSummary() {
    const total = migrationView.migrationTotal || 0;
    const migrated = migrationView.migrationMigrated || 0;
    const skipped = migrationView.migrationSkipped || 0;
    const failed = migrationView.migrationFailed || 0;

    return (
      <p className="text-[#6B7280] text-[13px]">
        {total} total &nbsp;·&nbsp; {migrated} migrated &nbsp;·&nbsp; {skipped} skipped
        &nbsp;·&nbsp; {failed} failed
      </p>
    );
  }

  function handleCopyLink() {
    if (!generatedLink) return;
    navigator.clipboard.writeText(generatedLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const team = request.teamId;

  return (
    <Sidepanel wid={600} pad="0" visible={!!visible} closeModal={closePanel}>
      <ContentHeader
        text={`${isReplacement ? 'Replacement' : 'Deletion'} Request`}
        closePanel={closePanel}
      />

      <div className="px-6 py-5 space-y-5">
        {/* Request Details */}
        <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden">
          <div className="px-5 py-3 space-y-0.5">
            <InfoRow label="Team">
              {team?.name || team?.username || team?.email || '-'}
            </InfoRow>
            <InfoRow label="Team ID" muted>{team?._id || '-'}</InfoRow>
            <InfoRow label="Current Stripe Account" muted>
              <span className="font-mono text-[12px]">
                {team?.billing?.stripeAccountId || 'None'}
              </span>
            </InfoRow>
            <InfoRow label="Request Type">
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${isReplacement ? 'bg-[#EDE9FE] text-[#5B21B6]' : 'bg-[#FEE2E2] text-[#991B1B]'}`}>
                {isReplacement ? 'Replacement' : 'Deletion'}
              </span>
            </InfoRow>
            <InfoRow label="Status"><StatusBadge status={request.status} /></InfoRow>
            <InfoRow label="Requested At" muted>
              {request.requestedAt ? new Date(request.requestedAt).toLocaleString() : '-'}
            </InfoRow>
            {request.approvedAt && (
              <InfoRow label="Approved At" muted>
                {new Date(request.approvedAt).toLocaleString()}
              </InfoRow>
            )}
            {request.approvedBy && (
              <InfoRow label="Approved By">
                {request.approvedBy?.name || request.approvedBy?.email || '-'}
              </InfoRow>
            )}
          </div>
        </div>

        {/* Reason */}
        <div>
          <SectionLabel>User Reason</SectionLabel>
          <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-4 py-3">
            <p className="text-[13px] text-[#374151] leading-relaxed">{request.reason || '-'}</p>
          </div>
        </div>

        {/* Rejection Reason */}
        {request.rejectionReason && (
          <div>
            <SectionLabel>Rejection Reason</SectionLabel>
            <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg px-4 py-3">
              <p className="text-[13px] text-[#991B1B] leading-relaxed">{request.rejectionReason}</p>
            </div>
          </div>
        )}

        {/* Account Replacement IDs (completed replacements) */}
        {isReplacement && request.status === 'completed' && (
          <div>
            <SectionLabel>Account Replacement</SectionLabel>
            <div className="bg-white border border-[#e5e7eb] rounded-xl px-5 py-3 space-y-0.5">
              <InfoRow label="Previous Account" muted>
                <span className="font-mono text-[12px]">{request.previousStripeAccountId || '-'}</span>
              </InfoRow>
              <InfoRow label="New Account" muted>
                <span className="font-mono text-[12px]">{request.newStripeAccountId || '-'}</span>
              </InfoRow>
            </div>
          </div>
        )}

        {/* Subscription Migration (completed replacements) */}
        {isReplacement && request.status === 'completed' && (
          <div>
            <SectionLabel>Subscription Migration</SectionLabel>
            <div className="space-y-3">
              {renderMigrationSummary()}
              {renderMigrationDetailRows()}

              {migrating ? (
                <div className="flex justify-center py-3">
                  <Spinner2 />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleMigrate}
                  className="w-full px-4 py-2.5 rounded-lg text-[13px] font-semibold text-white bg-[#635BFF] hover:bg-[#5148d4] transition-colors"
                >
                  Retry Now
                </button>
              )}

              {(migrationStatus || migrateResult) && (
                <div className={`px-4 py-3 rounded-xl border text-[13px] space-y-1 ${migrationStatus === 'failed' ? 'bg-[#FEF2F2] border-[#FCA5A5]' : 'bg-[#F8FAFC] border-[#E5E7EB]'}`}>
                  <p className="font-medium text-[#1a2332]">Latest Migration Snapshot</p>
                  {renderMigrationResultSummary()}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Generated Link (shown after approval) */}
        {generatedLink && (
          <div>
            <SectionLabel>Generated Replacement Link</SectionLabel>
            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg px-4 py-3 space-y-2">
              <p className="text-[12px] font-mono text-[#374151] break-all">{generatedLink}</p>
              {linkExpiry && (
                <p className="text-[11px] text-[#6B7280]">
                  Expires: {new Date(linkExpiry).toLocaleString()}
                </p>
              )}
              <button
                type="button"
                onClick={handleCopyLink}
                className="px-3 py-1.5 rounded-lg text-[12px] font-medium text-white bg-[#16A34A] hover:bg-[#15803D] transition-colors"
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>
        )}

        {/* Actions */}
        {isPending && (
          <div className="space-y-3">
            <SectionLabel>Admin Actions</SectionLabel>

            {loading && (
              <div className="flex justify-center py-4">
                <Spinner2 />
              </div>
            )}

            {!loading && (
              <>
                {isReplacement && !generatedLink && (
                  <button
                    type="button"
                    onClick={handleGenerateLink}
                    className="w-full px-4 py-2.5 rounded-lg text-[13px] font-semibold text-white bg-[#635BFF] hover:bg-[#5148d4] transition-colors"
                  >
                    Approve & Generate Replacement Link
                  </button>
                )}

                {isDeletion && (
                  <button
                    type="button"
                    onClick={handleApproveDeletion}
                    className="w-full px-4 py-2.5 rounded-lg text-[13px] font-semibold text-white bg-[#DC2626] hover:bg-[#B91C1C] transition-colors"
                  >
                    Approve Deletion — Disconnect Stripe Account
                  </button>
                )}

                {!showRejectForm ? (
                  <button
                    type="button"
                    onClick={() => setShowRejectForm(true)}
                    className="w-full px-4 py-2.5 rounded-lg text-[13px] font-semibold text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] transition-colors"
                  >
                    Reject Request
                  </button>
                ) : (
                  <div className="space-y-2">
                    <textarea
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2.5 text-[13px] text-[#1a2332] resize-none focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-400"
                      rows={3}
                      placeholder="Optional: reason for rejection"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      maxLength={500}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => { setShowRejectForm(false); setRejectReason(''); }}
                        className="flex-1 px-4 py-2 rounded-lg text-[13px] font-medium text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleReject}
                        className="flex-1 px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#DC2626] hover:bg-[#B91C1C] transition-colors"
                      >
                        Confirm Reject
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Sidepanel>
  );
};

export default StripeRequestPanel;
